import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Chip, 
  Button,
  CircularProgress,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EuroIcon from '@mui/icons-material/Euro';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PeopleIcon from '@mui/icons-material/People';
import { authService } from '../../services/authService';
import { formatDate } from '../../utils/dateUtils';
import { cloudinaryService } from '../../services/cloudinaryService';
import WarningIcon from '@mui/icons-material/Warning';

function OffreDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  const [offre, setOffre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [postulationDialog, setPostulationDialog] = useState({
    open: false,
    cv: null,
    lettreMotivation: null
  });
  const [cvError, setCvError] = useState('');
  const [lettreMotivationError, setLettreMotivationError] = useState('');
  const [nombreCandidatures, setNombreCandidatures] = useState(0);

  // Déterminer le texte et le chemin de retour en fonction de la page précédente
  const getRetourInfo = () => {
    const previousPath = location.state?.from || '/offres';
    const page = location.state?.page || 1;
    const tab = location.state?.tab || 'entreprises';
    const entrepriseId = location.state?.entrepriseId;
    
    switch (previousPath) {
      case `/entreprises/${entrepriseId}`:
        return {
          text: 'Retour à l\'entreprise',
          path: `/entreprises/${entrepriseId}`
        };
      case '/wishlist':
        return {
          text: 'Retour aux favoris',
          path: `/wishlist?page=${page}`
        };
      case '/candidatures':
        return {
          text: 'Retour aux candidatures',
          path: `/candidatures?page=${page}`
        };
      case '/stats':
        return {
          text: 'Retour aux statistiques',
          path: `/stats?tab=${tab}`
        };
      case '/suivi':
        return {
          text: 'Retour à l\'étudiant',
          path: `/etudiants/${id}/suivi?page=${page}`
        };
      default:
        return {
          text: 'Retour aux offres',
          path: `/offres?page=${page}`
        };
    }
  };

  const { text: retourText, path: retourPath } = getRetourInfo();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Récupérer les détails de l'offre et les stats en parallèle
        const [offreResponse, statsResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}`, {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}/stats`, {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          })
        ]);

        if (!offreResponse.ok) {
          throw new Error("Cette offre n'existe pas");
        }

        const offreData = await offreResponse.json();
        setOffre(offreData.offre);

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setNombreCandidatures(statsData.nombreCandidatures || 0);
        }

        // Si l'utilisateur est connecté en tant qu'étudiant
        if (currentUser && userRole === ROLES.ETUDIANT) {
          try {
            // Récupérer les candidatures et la wishlist en parallèle
            const [candidaturesResponse, wishlistResponse] = await Promise.all([
              fetch(`${process.env.REACT_APP_API_BASE_URL}/candidatures`, {
                headers: {
                  'Authorization': `Bearer ${authService.getToken()}`,
                }
              }),
              fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/my-wishlist`, {
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${authService.getToken()}`
                }
              })
            ]);

            if (candidaturesResponse.ok) {
              const candidaturesData = await candidaturesResponse.json();
              setHasApplied(candidaturesData.candidatures.some(
                candidature => candidature.offre.id_offres === parseInt(id)
              ));
            }

            if (wishlistResponse.ok) {
              const wishlistData = await wishlistResponse.json();
              setInWishlist(wishlistData.wishlist.some(offre => offre.id_offres === parseInt(id)));
            }
          } catch (error) {
            console.error('Erreur lors de la récupération des données étudiant:', error);
          }
        }

      } catch (err) {
        console.error("Erreur lors du chargement des données:", err);
        setError("Une erreur est survenue lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentUser, userRole]);

  const handleFileChange = async (event, type) => {
    const file = event.target.files[0];
    
    if (file) {
      // Vérification de la taille (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        if (type === 'cv') {
          setCvError('Le fichier est trop volumineux (max 5MB)');
        } else {
          setLettreMotivationError('Le fichier est trop volumineux (max 5MB)');
        }
        return;
      }

      // Vérification du type de fichier
      if (file.type !== 'application/pdf') {
        if (type === 'cv') {
          setCvError('Seuls les fichiers PDF sont acceptés');
        } else {
          setLettreMotivationError('Seuls les fichiers PDF sont acceptés');
        }
        return;
      }

      setPostulationDialog(prev => ({
        ...prev,
        [type]: file
      }));

      if (type === 'cv') {
        setCvError('');
      } else {
        setLettreMotivationError('');
      }
    }
  };

  const handleToggleWishlist = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/wishlist/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la modification de la wishlist');
      }

      setInWishlist(!inWishlist);
    } catch (error) {
      console.error("Erreur lors de la modification de la wishlist:", error);
    }
  };

  const handlePostuler = async () => {
    try {
      setLoading(true);
      
      // Upload avec les presets spécifiques et récupération des URLs optimisées
      const cvUrl = await cloudinaryService.uploadToCloudinary(postulationDialog.cv, 'cv');
      const lmUrl = await cloudinaryService.uploadToCloudinary(postulationDialog.lettreMotivation, 'lm');

      // Envoi des URLs optimisées au backend
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${id}/postuler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cv_url: cvUrl,
          lettre_motivation_url: lmUrl
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de la candidature');
      }

      setSuccess(true);
      setHasApplied(true);
      setTimeout(() => {
        setSuccess(false);
        setPostulationDialog({
          open: false,
          cv: null,
          lettreMotivation: null
        });
      }, 2000);
      
    } catch (error) {
      console.error('Erreur handlePostuler:', error);
      setError("Une erreur est survenue lors de l'envoi de votre candidature");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(retourPath)}
            variant="contained"
            color="primary"
            sx={{ mb: 4 }}
          >
            {retourText}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            top: 20, 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '300px'
          }}
        >
          Candidature envoyée avec succès !
        </Alert>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(retourPath)}
        variant="contained"
        color="primary"
        sx={{ mb: 4 }}
      >
        {retourText}
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {offre?.titre}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">
            {offre?.entreprises[0]?.nom || 'Entreprise inconnue'}
          </Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
          {offre?.description}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Technologies requises :
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {offre?.technologies.map((tech, index) => (
              <Chip 
                key={index}
                label={tech.nom_techno}
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  m: 0.5
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" />
            <Typography>
              Durée : {offre?.duree}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EuroIcon color="primary" />
            <Typography>
              {offre?.remuneration}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon color="primary" />
            <Typography>
              Début : {formatDate(offre?.dateDebut)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon color="primary" />
            <Typography>
              {offre?.localisation}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            <Typography>
              {nombreCandidatures} candidature{nombreCandidatures !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {/* Boutons d'action */}
        {currentUser && userRole === ROLES.ETUDIANT && (
          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleToggleWishlist}
              startIcon={inWishlist ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
            >
              {inWishlist ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => setPostulationDialog({ ...postulationDialog, open: true })}
              disabled={hasApplied}
            >
              {hasApplied ? 'Déjà postulé' : 'Postuler'}
            </Button>
          </Box>
        )}

        {currentUser && userRole !== ROLES.ETUDIANT && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Seuls les étudiants peuvent postuler aux offres
            </Typography>
          </Box>
        )}

        {!currentUser && (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
            >
              Se connecter pour postuler
            </Button>
          </Box>
        )}
      </Paper>

      {/* Dialog de postulation */}
      <Dialog
        open={postulationDialog.open}
        onClose={() => setPostulationDialog({ ...postulationDialog, open: false })}
      >
        <DialogTitle>Postuler à cette offre</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pour postuler, veuillez fournir votre CV et une lettre de motivation au format PDF.
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Les documents doivent faire une seule page chacun et ne pas dépasser 5 Mo.
            </Typography>
          </DialogContentText>
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 1 }}
            >
              {postulationDialog.cv ? 'Changer le CV' : 'Ajouter votre CV'}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'cv')}
              />
            </Button>
            {postulationDialog.cv && (
              <Typography variant="caption" color="primary">
                Fichier sélectionné : {postulationDialog.cv.name}
              </Typography>
            )}
            {cvError && (
              <Typography variant="caption" color="error">
                {cvError}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 1 }}
            >
              {postulationDialog.lettreMotivation ? 'Changer la lettre' : 'Ajouter votre lettre de motivation'}
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => handleFileChange(e, 'lettreMotivation')}
              />
            </Button>
            {postulationDialog.lettreMotivation && (
              <Typography variant="caption" color="primary">
                Fichier sélectionné : {postulationDialog.lettreMotivation.name}
              </Typography>
            )}
            {lettreMotivationError && (
              <Typography variant="caption" color="error">
                {lettreMotivationError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostulationDialog({ ...postulationDialog, open: false })}>
            Annuler
          </Button>
          <Button 
            onClick={handlePostuler}
            variant="contained"
            color="primary"
            disabled={!postulationDialog.cv || !postulationDialog.lettreMotivation || loading}
          >
            {loading ? 'Envoi en cours...' : 'Confirmer la candidature'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default OffreDetails; 